import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  IconButton,
  Alert
} from '@mui/material';
import { Close as CloseIcon, Favorite as FavoriteFilledIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { addToWishlistAsync } from '../features/wishlist/store/wishlistSlice';

/**
 * Simple Product Wishlist Dialog
 * 
 * Used to add existing products to the wishlist from various pages.
 * Does NOT support custom one-time products (use AddToWishlistDialog for that).
 * 
 * Props:
 *   open        {boolean}        - Dialog open state
 *   onClose     {() => void}     - Close handler
 *   product     {object}         - Product to add to wishlist
 *   onSuccess   {() => void}     - Optional callback after successful add
 */
const ProductWishlistDialog = ({ open, onClose, product, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const { loading: wishlistLoading, error: wishlistError } = useSelector(
    state => state.wishlist || { loading: false, error: null }
  );

  const [quantity, setQuantity] = React.useState(1);
  const [priority, setPriority] = React.useState('Medium');
  const [notes, setNotes] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setQuantity(1);
      setPriority('Medium');
      setNotes('');
      setLoading(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!product || quantity < 1) {
      return;
    }

    try {
      setLoading(true);

      const wishlistData = {
        ProductId: product.Id || product.ProductId,
        Quantity: quantity,
        Priority: priority || 'Medium',
        Notes: notes.trim() || null,
        SupplierId: product.SupplierId || product.DefaultSupplierId || null,
      };

      const result = await dispatch(addToWishlistAsync(wishlistData));

      if (addToWishlistAsync.fulfilled.match(result)) {
        toast.success(`${product.Name || product.ProductName} ${t('added_to_wishlist') || 'added to wishlist'}!`);
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(product);
        }
        
        handleClose();
      } else {
        throw new Error(result.payload || 'Failed to add to wishlist');
      }
    } catch (error) {
      console.error('❌ Error adding to wishlist:', error);
      toast.error(t('failed_to_add_to_wishlist') || 'Failed to add product to wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !wishlistLoading) {
      setQuantity(1);
      setPriority('Medium');
      setNotes('');
      onClose();
    }
  };

  const getTotalAmount = () => {
    if (!product) return '0.00';
    const price = product.SalePrice || product.Price || product.CostPrice || 0;
    return (price * quantity).toFixed(2);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FavoriteFilledIcon color="secondary" />
            <Typography variant="h6">{t('add_to_wishlist') || 'Add to Wishlist'}</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={loading || wishlistLoading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {product && (
          <>
            {/* Product Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {product.Name || product.ProductName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('sku') || 'SKU'}: {product.SKU || product.ProductSKU || 'N/A'}
                {(product.CategoryName || product.ProductCategory?.CategoryName) && (
                  <> • {t('category') || 'Category'}: {product.CategoryName || product.ProductCategory?.CategoryName}</>
                )}
              </Typography>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                €{Number(product.SalePrice || product.Price || product.CostPrice || 0).toFixed(2)}
              </Typography>
            </Box>

            {/* Quantity */}
            <TextField
              fullWidth
              label={t('quantity') || 'Quantity'}
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              helperText={
                (product.StockQuantity > 0 || product.CurrentStock > 0) 
                  ? `${t('available_stock') || 'Available stock'}: ${product.StockQuantity || product.CurrentStock}` 
                  : undefined
              }
              sx={{ mb: 2 }}
              disabled={loading || wishlistLoading}
            />

            {/* Priority */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('priority') || 'Priority'}</InputLabel>
              <Select
                value={priority || 'Medium'}
                label={t('priority') || 'Priority'}
                onChange={(e) => setPriority(e.target.value)}
                disabled={loading || wishlistLoading}
              >
                <MenuItem value="Low">{t('low') || 'Low'}</MenuItem>
                <MenuItem value="Medium">{t('medium') || 'Medium'}</MenuItem>
                <MenuItem value="High">{t('high') || 'High'}</MenuItem>
              </Select>
            </FormControl>

            {/* Notes */}
            <TextField
              fullWidth
              label={t('notes_optional') || 'Notes (optional)'}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('add_any_notes_about_this_wishlist_item') || 'Add any notes about this wishlist item...'}
              sx={{ mb: 2 }}
              disabled={loading || wishlistLoading}
            />

            {/* Total Amount */}
            <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="body2" color="text.secondary">
                {t('total_amount') || 'Total amount'}
              </Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                €{getTotalAmount()}
              </Typography>
            </Box>

            {/* Error Display */}
            {wishlistError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {wishlistError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading || wishlistLoading}
        >
          {t('cancel') || 'Cancel'}
        </Button>
        <Button 
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || wishlistLoading || !product || quantity < 1}
          startIcon={(loading || wishlistLoading) ? <CircularProgress size={20} /> : <FavoriteFilledIcon />}
        >
          {(loading || wishlistLoading) ? (t('adding') || 'Adding...') : (t('add_to_wishlist') || 'Add to Wishlist')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductWishlistDialog;
